import assertNever from "assert-never";
import { Instant } from "@js-joda/core";
import * as React from "react";
import { API } from "../API";
import { AppModalAction, ErrorModal } from "../components/Generic/AppModal/AppModal";
import {
  TrainingCenterBundleItemScreen,
  TrainingCenterBundleItemScreenMethods,
} from "../components/TrainingCenter/TrainingCenterBundleItemScreen";
import { TrainingCenterLanguagePreference } from "../components/TrainingCenter/TrainingCenterLanguageLookup";
import WithCaregiver from "../components/WithCaregiver";
import { isLoading, loading, Loading } from "../Loading";
import {
  CaregiverImportedVisitDetails,
  TrainingCenterCaregiverPDFBundleItemWatchSegmentParams,
  TrainingCenterCaregiverQuestionAnswer,
  TrainingCenterCaregiverSubmitTestResult,
  TrainingCenterCaregiverVideoBundleItemWatchSegmentParams,
  TrainingCenterDisplayLanguage,
} from "../messages";
import {
  AgencyMemberId,
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleItemId,
  TrainingCenterBundleItemVideoEngmtQuestionId,
} from "../messages/ObjectIds";
import { retryableModalNetworkRequest } from "../NetworkRequest";
import { STRINGS } from "../strings";
import { AppState } from "../system/AppState";
import {
  EngagementChosenAnswer,
  TrainingCenterBundleItemVideoEngagementQuestion,
} from "../system/AppState/AppStateTrainingCenter";
import { delay } from "../system/delay";
import { EventSourceBinding } from "../system/EventSource";
import { translate } from "../translate";
import { HashMap } from "../utils/HashMap";
import { HashSet } from "../utils/HashSet";
import { List } from "../utils/List";
import { ActivityStateMixin, ActivityStateMixinState } from "./ActivityStateMixin";

const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

interface TrainingCenterBundleItemActivityProps {
  api: API;
  appState: AppState;
  languagePreference: TrainingCenterLanguagePreference;
  bundleDueDateId: TrainingCenterBundleDueDateId;
  bundleItemId: TrainingCenterBundleItemId;
  allLanguages: List<TrainingCenterDisplayLanguage>;

  onLanguagePreferenceChange: (languagePreference: TrainingCenterLanguagePreference) => void;
  onBackClick: (animation: string) => void;
  onAgencyMemberChatClick: (agencyMemberId: AgencyMemberId, initialMessage: string) => void;
  onSubmitTestSuccessConfirm: () => void;
  onBundleItemNotFound: () => void;
  onRequestNavigateToNewChat: () => void;
}

interface TrainingCenterBundleItemActivityState {
  mixin: ActivityStateMixinState;
  submitting: boolean;
  currentlyDuringShift: boolean;
  chosenEngagementAnswers: HashMap<
    TrainingCenterBundleItemVideoEngmtQuestionId,
    EngagementChosenAnswer
  >;
  closedEngagementQuestions: HashSet<TrainingCenterBundleItemVideoEngmtQuestionId>;
  aspectRatio: number;
  isFullscreen: boolean;
}

export class TrainingCenterBundleItemActivity extends React.Component<
  TrainingCenterBundleItemActivityProps,
  TrainingCenterBundleItemActivityState
> {
  constructor(props: TrainingCenterBundleItemActivityProps) {
    super(props);

    this.activityStateMixin = new ActivityStateMixin(
      (state) =>
        this.setState((_prevState: TrainingCenterBundleItemActivityState) => ({
          mixin: state,
        })),
      this.props.appState
    );

    this.onTrainingCenterUpdatedBinding = props.appState.onTrainingCenterUpdated.register(() => {
      this.forceUpdate();
    });

    this.state = {
      mixin: this.activityStateMixin.initialState(),
      submitting: false,
      currentlyDuringShift: calcIsCurrenctlyDuringShift(
        props.appState.closestScheduledShifts,
        isLoading(props.appState.scheduledVisits)
          ? loading()
          : props.appState.scheduledVisits.importedVisitDetails
      ),
      chosenEngagementAnswers: HashMap.empty(),
      closedEngagementQuestions: HashSet.empty(),
      aspectRatio: DEFAULT_VIDEO_ASPECT_RATIO,
      isFullscreen: false,
    };

    this.props.appState.refreshTrainingCenterBundle(props.bundleDueDateId);

    this.intervalBinding = setInterval(() => {
      this.setState((_prevState) => ({
        currentlyDuringShift: calcIsCurrenctlyDuringShift(
          props.appState.closestScheduledShifts,
          isLoading(props.appState.scheduledVisits)
            ? loading()
            : props.appState.scheduledVisits.importedVisitDetails
        ),
      }));
    }, 10000);
  }

  private onTrainingCenterUpdatedBinding: EventSourceBinding;

  private activityStateMixin: ActivityStateMixin;

  private intervalBinding: any;

  componentWillUnmount(): void {
    this.activityStateMixin.componentWillUnmount();

    this.onTrainingCenterUpdatedBinding.unregister();

    clearInterval(this.intervalBinding);
  }

  handleChangeVideoAspectRatio = (aspectRatio: number) => {
    this.setState({ aspectRatio: aspectRatio });
  };

  handleChangeFullScreen = (isFullscreen: boolean) => {
    this.setState({ isFullscreen: isFullscreen });
  };

  handleWatchVideoSegment = (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    language: TrainingCenterDisplayLanguage,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverVideoBundleItemWatchSegmentParams
  ): void => {
    this.props.api
      .trainingCenterVideoBundleItemWatchSegment(
        trainingCenterBundleDueDateId,
        trainingCenterBundleItemId,
        language,
        clientUniqueId,
        segment
      )
      .then(
        (watchData) => {
          this.props.appState.trainingCenterUpdateVideoBundleItemWatchData(
            trainingCenterBundleDueDateId,
            trainingCenterBundleItemId,
            language,
            watchData
          );
        },
        (err) => {
          // TODO It is probably a good idea to try to resend the
          // segment, but it is not too much of a tragedy if it is lost:
          //
          //     - Long watch segments are sent repeatedly so if one is missed the next one will cover it
          //     - If it was a very short segment that was lost then we have only lossed a small amount of data
          console.warn("ERROR sending video watch segment:", err);
        }
      );
  };

  handleWatchPDFSegment = (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    clientUniqueId: number,
    segment: TrainingCenterCaregiverPDFBundleItemWatchSegmentParams
  ): void => {
    this.props.api
      .trainingCenterPDFBundleItemWatchSegment(
        trainingCenterBundleDueDateId,
        trainingCenterBundleItemId,
        clientUniqueId,
        segment
      )
      .then(
        (watchData) => {
          this.props.appState.trainingCenterUpdatePDFBundleItemWatchData(
            trainingCenterBundleDueDateId,
            trainingCenterBundleItemId,
            watchData
          );
        },
        (err) => {
          // TODO It is probably a good idea to try to resend the
          // segment, but it is not too much of a tragedy if it is lost:
          //
          //     - Long watch segments are sent repeatedly so if one is missed the next one will cover it
          //     - If it was a very short segment that was lost then we have only lossed a small amount of data
          console.warn("ERROR sending video watch segment:", err);
        }
      );
  };

  handleQuestionAnswerClick = (
    trainingCenterBundleDueDateId: TrainingCenterBundleDueDateId,
    trainingCenterBundleItemId: TrainingCenterBundleItemId,
    params: TrainingCenterCaregiverQuestionAnswer
  ): void => {
    // TODO There is a possible race condition here! Earlier requests may
    // (on rare occasions) arrive later on the server causing the saved
    // answer to be wrong. Solution: There should be a single request
    // queue that always submits these requests to the server only after
    // the previous request has completed. (Bonus: we will get get
    // automatic retries in the background (because we will be forced to
    // implement it:))
    this.props.api
      .trainingCenterCaregiverSaveDraftTestAnswer(
        trainingCenterBundleDueDateId,
        trainingCenterBundleItemId,
        params
      )
      .then(
        () => {
          // Done
        },
        (err) => {
          // TODO ... (?)
          console.warn("ERROR saving caregiver draft test answer:", err);
        }
      );
  };

  handleSubmitTest = async (
    bundleDueDateId: TrainingCenterBundleDueDateId,
    bundleItemId: TrainingCenterBundleItemId,
    testVersionNum: number,
    language: TrainingCenterDisplayLanguage,
    caregiverSignatureDataURL: string,
    questionAnswers: TrainingCenterCaregiverQuestionAnswer[]
  ): Promise<void> => {
    this.setState((prevState) => ({
      ...prevState,
      submitting: true,
    }));
    // Small delay to give a nice feeling that the test is actually being checked...
    await delay(1500);

    let result: TrainingCenterCaregiverSubmitTestResult;
    try {
      result = await this.props.api.trainingCenterBundleItemSubmitTest(
        bundleDueDateId,
        bundleItemId,
        {
          caregiverSignatureDataURL: caregiverSignatureDataURL,
          testVersionNum: testVersionNum,
          language: language,
          questionAnswers: questionAnswers,
        }
      );
    } catch (err) {
      let initial = true;
      const result2 = await retryableModalNetworkRequest<TrainingCenterCaregiverSubmitTestResult>(
        async () => {
          if (initial) {
            initial = false;
            throw err;
          } else {
            return this.props.api.trainingCenterBundleItemSubmitTest(
              bundleDueDateId,
              bundleItemId,
              {
                caregiverSignatureDataURL: caregiverSignatureDataURL,
                testVersionNum: testVersionNum,
                language: language,
                questionAnswers: questionAnswers,
              }
            );
          }
        },
        false
      );
      if (result2 === null) {
        return;
      }
      result = result2;
    } finally {
      this.setState((prevState) => ({
        ...prevState,
        submitting: false,
      }));
    }

    console.log(result);
    switch (result.type) {
      case "Success":
        // TODO Better to update the bundle directly with result
        this.props.appState.refreshTrainingCenterBundle(this.props.bundleDueDateId);
        break;
      case "AlreadyCompletedBundleItem":
      case "BundleNotVisibleToCaregiver":
      case "ExemptFromBundleItem":
      case "NotFound":
      case "OutdatedTestVersion":
      case "TooManyFailedAttempts":
      case "TestNotUnlocked":
      case "QuestionAnswersInvalid":
        break;
      default:
        assertNever(result);
    }
    if (this.trainingCenterBundleItemScreenRef !== null) {
      this.trainingCenterBundleItemScreenRef.notifyTestSubmissionResult(result);
    }
  };

  private trainingCenterBundleItemScreenRef: TrainingCenterBundleItemScreenMethods | null = null;

  handleTrainingCenterBundleItemScreenRef = (
    ref: TrainingCenterBundleItemScreenMethods | null
  ): void => {
    this.trainingCenterBundleItemScreenRef = ref;
  };

  handleEngagementAnswerClick = (
    question: TrainingCenterBundleItemVideoEngagementQuestion,
    answerValue: string,
    atSecond: number
  ) => {
    this.setState((state) => ({
      chosenEngagementAnswers: state.chosenEngagementAnswers.set(question.questionId, {
        value: answerValue,
        atSecond: atSecond,
      }),
    }));
  };

  handleCloseAnswerClick = (question: TrainingCenterBundleItemVideoEngagementQuestion) => {
    this.setState((state) => ({
      closedEngagementQuestions: state.closedEngagementQuestions.add(question.questionId),
    }));
  };

  getBundle() {
    const bundle = isLoading(this.props.appState.appTrainingCenterState)
      ? loading()
      : this.props.appState.appTrainingCenterState.bundles.find(
          (b) => b.bundleDueDateId === this.props.bundleDueDateId
        );

    return bundle;
  }

  getBundleItem() {
    const bundle = this.getBundle();
    const bundleItem = isLoading(bundle)
      ? loading()
      : bundle === undefined
      ? undefined
      : lookupWithDefault(
          bundle.bundleItems.find((item) => item.bundleItemId === this.props.bundleItemId),
          (x) => x,
          undefined
        );

    return bundleItem;
  }

  render(): JSX.Element {
    const bundle = this.getBundle();

    const bundleItem = this.getBundleItem();

    const agency = isLoading(this.state.mixin.appAgenciesState)
      ? loading()
      : isLoading(bundle)
      ? loading()
      : bundle === undefined
      ? undefined
      : lookupWithDefault(
          this.state.mixin.appAgenciesState.agencies.get(bundle.agencyId),
          (a) => a.agency,
          undefined
        );

    if (bundleItem === undefined) {
      const modalActions: AppModalAction[] = [
        {
          key: "OK",
          variant: "soft",
          color: "primary",
          children: "OK",
          onClick: () => this.props.onBundleItemNotFound(),
        },
      ];
      return (
        <ErrorModal
          title={translate(STRINGS.NOT_FOUND)}
          message={translate(STRINGS.BUNDLE_ITEM_NOT_FOUND)}
          actions={modalActions}
          isOpen={true}
          onRequestClose={() => this.props.onBundleItemNotFound()}
        />
      );
    }

    return (
      <WithCaregiver appState={this.props.appState}>
        {(caregiver) => (
          <TrainingCenterBundleItemScreen
            key={`${this.props.bundleDueDateId}-${this.props.bundleItemId}`}
            ref={this.handleTrainingCenterBundleItemScreenRef}
            currentlyDuringShift={this.state.currentlyDuringShift}
            languagePreference={this.props.languagePreference}
            bundleDueDateId={this.props.bundleDueDateId}
            bundleItemId={this.props.bundleItemId}
            agency={agency}
            bundle={bundle}
            bundleItem={bundleItem}
            submitting={this.state.submitting}
            allLanguages={this.props.allLanguages}
            agencies={
              isLoading(this.props.appState.appAgenciesState)
                ? loading()
                : this.props.appState.appAgenciesState.agencies
            }
            chosenEngagementAnswers={this.state.chosenEngagementAnswers}
            closedEngagementQuestions={this.state.closedEngagementQuestions}
            getOffices={() =>
              this.props.api.getTrainingCenterBundleAndCaregiverOffices(this.props.bundleDueDateId)
            }
            isFullscreen={this.state.isFullscreen}
            aspectRatio={this.state.aspectRatio}
            shouldShowNewChat={caregiver.shouldShowNewChat}
            onClickEngagementAnswer={this.handleEngagementAnswerClick}
            onClickCloseAnswer={this.handleCloseAnswerClick}
            onLanguagePreferenceChange={this.props.onLanguagePreferenceChange}
            onBackClick={this.props.onBackClick}
            onAgencyMemberChatClick={this.props.onAgencyMemberChatClick}
            onQuestionAnswerClick={this.handleQuestionAnswerClick}
            onWatchVideoSegment={this.handleWatchVideoSegment}
            onWatchPDFSegment={this.handleWatchPDFSegment}
            onSubmitTest={this.handleSubmitTest}
            onSubmitTestSuccessConfirm={this.props.onSubmitTestSuccessConfirm}
            onChangeFullscreen={this.handleChangeFullScreen}
            onChangeVideoAspectRatio={this.handleChangeVideoAspectRatio}
            onRequestNavigateToNewChat={this.props.onRequestNavigateToNewChat}
          />
        )}
      </WithCaregiver>
    );
  }
}

function calcIsCurrenctlyDuringShift(
  closestScheduledShifts: [Instant, Instant][] | Loading,
  importedVisitsList: List<CaregiverImportedVisitDetails> | Loading
) {
  if (isLoading(closestScheduledShifts)) {
    return false;
  }

  if (isLoading(importedVisitsList)) {
    return false;
  }

  const now = Instant.now();

  const importedVisits = importedVisitsList.map<[Instant, Instant]>((shift) => {
    return [shift.startTimeUTC, shift.endTimeUTC];
  });

  for (const shift of [...closestScheduledShifts, ...importedVisits]) {
    if (shift[0].isBefore(now) && shift[1].isAfter(now)) {
      return true;
    }
  }

  return false;
}

function lookupWithDefault<T, S>(val: T | undefined, lookup: (val: T) => S, def: S): S {
  if (val === undefined) {
    return def;
  } else {
    return lookup(val);
  }
}
